import React, { useMemo } from 'react';
import FadeIn from 'react-native-fade-in-image';
import { Image, TouchableOpacity, View, StyleSheet, ScrollView, useWindowDimensions } from 'react-native';
import type { ImageStyle } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import NavBar from 'src/presentation/components/organisms/NavBar';
import Heading from 'src/presentation/components/atoms/Heading';
import Paragraph from 'src/presentation/components/atoms/Paragraph';
import Cta from 'src/presentation/components/atoms/Cta';
import { useFormStore } from 'src/domain/stores/useFormStore';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import Images from 'src/core/utils/Images';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Constants from 'src/core/utils/Constants';

function LoginScreen() {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const formStore = useFormStore();
	const eventStore = useEventStore();
	const insets = useSafeAreaInsets();
	const contentWidth = useContentWidth();
	// The header image fills the screen down to the buttons, so its height is the window's.
	const { height: windowHeight } = useWindowDimensions();

	const sizedStyles = useMemo(
		(): { img: ImageStyle } => ({
			img: {
				width: contentWidth,
				height: windowHeight - ctaContainerHeight - insets.bottom,
			},
		}),
		[contentWidth, windowHeight, insets.bottom],
	);

	return (
		<View style={styles.root}>
			{formStore.loginFromForm ? (
				<NavBar
					backBtn={true}
					backStep={2}
					bgImage={eventStore.activeTheme.header_bg_image}
					bgcolor={eventStore.activeTheme.header_bg_color}
					color={eventStore.activeTheme.header_text_color}
					editBtn={false}
					statusBarStyle={eventStore.activeTheme.statusbar_color}
					title={Translations.buttonLogin}
				/>
			) : (
				<NavBar
					backBtn={true}
					bgcolor={Colors.primary}
					centerTitle={true}
					color={Colors.black}
					editBtn={false}
					title="Topicus Events"
				/>
			)}

			<ScrollView style={styles.scroll}>
				<FadeIn>
					<Image resizeMode="cover" source={Images.loginHeader} style={sizedStyles.img} />
				</FadeIn>
				<View style={styles.contentContainer}>
					<Heading text={Translations.titleLoginOrRegister} />
					<Paragraph text={Translations.textLoginDescription} />
				</View>
				<View style={styles.ctaContainer}>
					<TouchableOpacity
						onPress={() => {
							navigation.navigate(Constants.screenLoginForm);
						}}
					>
						<Cta
							buttonStyle="primaryBordered"
							label={Translations.buttonLogin}
							labelColor={Colors.secondary}
							upperCase={true}
						/>
					</TouchableOpacity>
					<TouchableOpacity
						onPress={() => {
							navigation.navigate(Constants.screenRegistrationForm);
						}}
					>
						<Cta buttonStyle="primary" label={Translations.buttonRegisterLower} labelColor={Colors.white} upperCase={true} />
					</TouchableOpacity>
				</View>
			</ScrollView>
		</View>
	);
}

export default LoginScreen;

const ctaContainerHeight = 380;

const styles = StyleSheet.create({
	root: {
		flex: 1,
	},
	scroll: {
		backgroundColor: Colors.white,
	},
	contentContainer: {
		paddingLeft: Dimens.padding20,
		paddingTop: Dimens.padding20,
		marginBottom: Dimens.margin30,
	},
	ctaContainer: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		justifyContent: 'center',
	},
});
